import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Menu, ChevronRight, Eye } from 'lucide-react'
import { useAuth, useWorkspaceOrgId } from '../lib/auth'
import { pushRecentOrg } from '../lib/recentOrgs'
import { useViewAs, ViewAsContext } from '../lib/useViewAs'
import { moduleForPath, shellLabel } from '../lib/navigation'
import { useInterval } from '../lib/useInterval'
import { apiGet } from '../lib/api'
import { useNotifications } from '../lib/useNotifications'
import { appShellPresence } from '../lib/appShellPresence'
import { useNewVersion } from '../lib/appVersion'
import type { DashboardResponse } from '../types'
import Sidebar, { type NavBadges } from './Sidebar'
import ShellLayout from './sidebar/ShellLayout'
import ClientSwitcher from './ClientSwitcher'
import NotificationBell from './NotificationBell'
import ProfileCompletionIndicator from './ProfileCompletionIndicator'
import LanguageToggle from './LanguageToggle'
import AssessmentStatusBar from './AssessmentStatusBar'
import { Toaster, toast } from './ui/toast'
import { Button } from './ui/button'
import { ClaraPanel, ClaraTrigger, isClaraShortcut } from './chat/ClaraPanel'

// ENG-195 + ENG-170 final phase — the operator shell: sidebar + contextual
// header + the ⌘K switcher wiring, plus everything ai-assessment's local
// composition root used to supply (the /api/v1/dashboard badge poll,
// notification polling, the org-profile completion indicator, the toaster, and
// the FloatingLanguageToggle stand-down).
//
// The slot props are gone because there is no second composition layer left:
// ai-assessment has no frontend of its own. The seam ENG-202 needs is one level
// down — second-brain composes from Sidebar/ShellLayout/SidebarFrame, which are
// still generic and know nothing about these endpoints.

const COLLAPSE_KEY = 'kx-sidebar-collapsed'

export default function AppShell() {
  const { t } = useTranslation('admin')
  const { me } = useAuth()
  const orgId = useWorkspaceOrgId()
  const [switcherOpen, setSwitcherOpen] = useState(false)
  const { pathname } = useLocation()
  // Remembered per client, so switching clients closes the panel.
  const [claraOrgId, setClaraOrgId] = useState<string | null>(null)
  // After the panel closes, focus goes back to the circle it came from (once, not on later remounts).
  const claraReturnFocus = useRef(false)
  const [loadedBadges, setBadges] = useState<NavBadges>({})
  const [loadedOrgName, setOrgName] = useState<string | null>(null)
  const [loadedModules, setModules] = useState<string[] | null>(null)
  // Derived, not reset in the effect: both are meaningless outside a workspace,
  // so gate on orgId at the point of use rather than clearing state on the way out.
  const badges = orgId ? loadedBadges : {}
  const orgName = orgId ? loadedOrgName : null
  const enabledModules = orgId ? loadedModules : null
  // Polled once here; both sidebar bells (desktop + drawer) share it (ENG-129).
  const notifications = useNotifications()
  const viewAs = useViewAs(me, orgId)
  const newVersion = useNewVersion()
  const staleAt = useRef<string | null>(null)

  // A newer frontend is out: say so once, and load it on the next move to another screen.
  useEffect(() => {
    if (!newVersion) return
    if (staleAt.current === null) {
      staleAt.current = pathname
      toast(t('appVersion.available'), { onClick: () => window.location.reload() })
    } else if (staleAt.current !== pathname) {
      window.location.reload()
    }
  }, [newVersion, pathname, t])

  // Signal that admin chrome is present so the global FloatingLanguageToggle
  // stands down (the header shows its own toggle). useLayoutEffect so the swap
  // happens before paint — no one-frame flash of a duplicate toggle. ENG-111.
  useLayoutEffect(() => {
    appShellPresence.set(true)
    return () => appShellPresence.set(false)
  }, [])

  // ⌘K opens the client switcher (ENG-116). Global search takes this over when
  // it ships; the switcher keeps its sidebar triggers.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setSwitcherOpen((o) => !o)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Deep links populate the switcher's Recent group too, not just card clicks.
  // Gated on orgName as well as orgId: ENG-68 recents carry the name so the
  // switcher never resolves ids against the client list, and the name only
  // arrives with the dashboard response below.
  useEffect(() => {
    if (orgId && orgName) pushRecentOrg({ id: Number(orgId), name: orgName })
  }, [orgId, orgName])

  // Badges decorate client-zone nav items and orgName feeds the breadcrumb +
  // switcher card — all of it exists only inside a workspace, so don't call at
  // all without orgId.
  const loadBadges = () => {
    if (!orgId) return
    apiGet<DashboardResponse>('/api/v1/dashboard', orgId)
      .then((d) => {
        setBadges({ assessments: d.metrics.awaiting_action > 0, alerts: d.metrics.sla_alerts > 0 })
        setOrgName(d.organization.name)
        setModules(d.organization.enabled_modules ?? null)
      })
      .catch(() => { setOrgName(null); setModules(null) })
  }
  useEffect(loadBadges, [orgId])
  useInterval(loadBadges, 60_000)

  // Dashboard › client (its Clara) › module › the menu item this page sits under.
  const crumbs = (org: string, name: string): { label: string; to?: string }[] => {
    const list: { label: string; to?: string }[] = [
      { label: t('shellNav.items.platformHome'), to: '/home' },
      { label: name, to: `/chat?org_id=${org}` },
    ]
    const mod = moduleForPath(pathname, 'client')
    if (!mod || pathname === '/chat') return list
    list.push({ label: shellLabel('modules', mod.key) })
    const item = mod.groups.flatMap((g) => g.items)
      .filter((i) => i.to && !i.query && (pathname === i.to || pathname.startsWith(`${i.to}/`)))
      .sort((a, b) => (b.to?.length ?? 0) - (a.to?.length ?? 0))[0]
    if (item) list.push({ label: shellLabel('items', item.key), to: `${item.to}?org_id=${org}` })
    return list
  }

  const headerRight: ReactNode = (
    <>
      {orgId && <ProfileCompletionIndicator />}
      <LanguageToggle />
    </>
  )

  const claraAvailable = !!orgId && !!enabledModules?.includes('second_brain') && !/\/chat$/.test(pathname)
  const claraOpen = claraAvailable && claraOrgId === orgId

  return (
    <ViewAsContext.Provider value={viewAs}>
    <ClaraShortcut enabled={claraAvailable && !claraOpen} onOpen={() => setClaraOrgId(orgId)} />
    <ShellLayout
      collapseKey={COLLAPSE_KEY}
      sidebarWidth="w-[328px]"
      railWidth="w-[88px]"
      renderSidebar={({ collapsed, canCollapse, onToggleCollapse, closeMobile }) => (
        <Sidebar
          collapsed={collapsed}
          canCollapse={canCollapse}
          onToggleCollapse={onToggleCollapse}
          badges={badges}
          orgName={orgName}
          enabledModules={enabledModules}
          onOpenSwitcher={() => { closeMobile(); setSwitcherOpen(true) }}
          bell={me?.user ? <NotificationBell notifications={notifications} /> : undefined}
          viewAs={viewAs}
        />
      )}
      renderHeader={({ openMobile }) => (
        // Slim contextual bar (ENG-129): breadcrumb when inside a workspace,
        // minimal global utilities on the right. Page titles/greetings live in
        // the pages themselves. The strip below it marks a "View as" preview.
        <>
        <header className="flex h-[var(--app-header)] shrink-0 items-center justify-between gap-3 border-b px-5 md:px-7 print:hidden">
          <div className="flex min-w-0 items-center gap-3">
            <button className="relative text-muted-foreground md:hidden" onClick={openMobile} title={t('shell.menu')}>
              <Menu className="size-5" />
              {notifications.unread > 0 && <span className="absolute -right-1 -top-1 size-2 rounded-full bg-danger" />}
            </button>
            {orgId && orgName && (
              <nav aria-label={t('shellNav.breadcrumbs')} className="flex min-w-0 items-center gap-1.5 text-compact">
                {crumbs(orgId, orgName).map((c, idx, all) => (
                  <span key={c.label} className="flex min-w-0 items-center gap-1.5">
                    {idx > 0 && <ChevronRight className="size-3.5 shrink-0 text-muted-foreground/60" />}
                    {c.to && idx < all.length - 1 ? (
                      <Link to={c.to} className="shrink-0 truncate text-muted-foreground transition-colors hover:text-foreground">{c.label}</Link>
                    ) : (
                      <span className="truncate font-medium">{c.label}</span>
                    )}
                  </span>
                ))}
              </nav>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">{headerRight}</div>
        </header>
        <AssessmentStatusBar />
        {viewAs.previewing && (
          <div className="flex shrink-0 items-center justify-between gap-3 border-b bg-mist px-5 py-1.5 text-body md:px-7 print:hidden">
            <span className="flex min-w-0 items-center gap-2">
              <Eye aria-hidden className="size-4 shrink-0 text-brand" />
              <span className="truncate">{t(pathname === '/chat' ? 'shellNav.viewAs.bannerClara' : 'shellNav.viewAs.bannerMenu', { role: t(`shellNav.viewAs.${viewAs.role}`) })}</span>
            </span>
            <Button variant="ghost" size="sm" onClick={() => viewAs.setRole(viewAs.realRole)}>{t('shellNav.viewAs.back')}</Button>
          </div>
        )}
        </>
      )}
      // Room under the page so the circle never covers the last row's actions.
      mainClassName={claraAvailable && !claraOpen ? 'flex-1 overflow-y-auto px-5 pt-6 pb-24 md:px-7 md:pt-7 print:overflow-visible' : undefined}
      aside={claraOpen
        ? <ClaraPanel orgId={orgId!} onClose={() => { claraReturnFocus.current = true; setClaraOrgId(null) }} />
        : claraAvailable ? <ClaraTrigger clientName={orgName} takeFocus={() => { const v = claraReturnFocus.current; claraReturnFocus.current = false; return v }} onOpen={() => setClaraOrgId(orgId)} /> : null}
      overlays={
        <>
          <ClientSwitcher open={switcherOpen} onOpenChange={setSwitcherOpen} viewAs={viewAs} />
          <Toaster />
        </>
      }
    />
    </ViewAsContext.Provider>
  )
}

function ClaraShortcut({ enabled, onOpen }: { enabled: boolean; onOpen: () => void }) {
  const onOpenRef = useRef(onOpen)
  useEffect(() => { onOpenRef.current = onOpen })
  useEffect(() => {
    if (!enabled) return
    const onKey = (e: KeyboardEvent) => { if (isClaraShortcut(e)) { e.preventDefault(); onOpenRef.current() } }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [enabled])
  return null
}
